import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, takeUntil } from 'rxjs';
import {
  ConfirmPreliminaryOrderResult,
  DeleteOrderDialogResult,
  applyConfirmedPreliminaryOrder,
  applyCreatedOrdersToServices,
  applyDeleteOrderDialogResult,
  applyFreezeDeadlineToServices,
  applyOrderUpdateToServices,
  canShowOrderPdf,
  formatScheduledPaymentDate,
  formatScheduledPaymentDateDisplay,
  getOrderMutationBlockedMessage,
  getServiceOrderId,
  getServiceOrderNumber,
  isOrderEditable,
  isOrderStatusDeletable,
  isOrderStatusPreliminary,
  isPdfReady,
} from 'src/app/entities/order';
import { OrderConfirmDialogComponent } from 'src/app/features/service-order/confirm-preliminary/ui/order-confirm-dialog/order-confirm-dialog.component';
import {
  CREATE_ORDER_PANEL_CLASS,
  buildCreateOrderDialogConfig,
} from 'src/app/features/service-order/create-order/ui/create-order/create-order-dialog.config';
import {
  CreateOrderComponent,
  CreateOrderDialogResult,
} from 'src/app/features/service-order/create-order/ui/create-order/create-order.component';
import { DialogDeleteOSComponent } from 'src/app/features/service-order/delete-service-order/ui/dialog-delete-os/dialog-delete-os.component';
import {
  OrderFreezeDeadlineDialogComponent,
  OrderFreezeDeadlineDialogResult,
} from 'src/app/features/service-order/edit-freeze-deadline/ui/order-freeze-deadline-dialog/order-freeze-deadline-dialog.component';
import {
  DialogEditFechaPagoProgramadaComponent,
  buildEditFechaPagoDialogConfig,
} from 'src/app/features/service-order/edit-scheduled-payment-date';
import {
  OrderPdfDialogComponent,
  OrderPdfDialogResult,
} from 'src/app/features/service-order/order-pdf/ui/order-pdf-dialog/order-pdf-dialog.component';
import { OrderRecalcHistoryDialogComponent } from 'src/app/features/service-order/recalc-history/ui/order-recalc-history-dialog/order-recalc-history-dialog.component';
import { PicnicService } from 'src/app/services/api/picnic.service';
import { DialogProviderMultipleComponent } from '../dialog-provider-multiple/dialog-provider-multiple.component';
import {
  CuadroPicnicItem,
  PicnicOperationalServiceSummary,
  PicnicServiceDetailItem,
} from '../models/cuadro-picnic.model';
import {
  OperationalServiceModalType,
  formatLocalDateToDisplay,
  resolveOperationalServiceFilters,
  resolveOperationalServiceMetadata,
  resolveOriginalServiceSummary,
} from '../utils';

/**
 * Contract defining input data injected into ServiciosOperativosModalComponent.
 */
export interface OperationalServicesModalData {
  picnicItemRow: CuadroPicnicItem;
  modalType?: OperationalServiceModalType;
  customTitle?: string;
  customIcon?: string;
  servicesList?: PicnicServiceDetailItem[];
}

/**
 * Backward-compatible type alias.
 */
export type ServiciosOperativosModalData = OperationalServicesModalData;

/**
 * Modal dialog component managing operational services (Empresas de Transporte, Asistentes de Picnic, Bañero).
 * Provides capabilities to inspect service assignments, select single or multiple services, change providers via Sourcing catalog,
 * and create, view (PDF preview/download), edit scheduled payment dates, and delete Órdenes de Servicio (O.S.).
 */
@Component({
  selector: 'app-servicios-operativos-modal',
  templateUrl: './servicios-operativos-modal.component.html',
  styleUrls: ['./servicios-operativos-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiciosOperativosModalComponent implements OnInit, OnDestroy {
  private readonly _unsubscribeOnDestroy$: Subject<boolean> =
    new Subject<boolean>();

  /**
   * Set tracking selected service UUID identifiers for batch operations.
   */
  public readonly selectedServiceIdentifiersSet = new Set<string>();

  /**
   * Dynamically loaded operational services array.
   */
  public loadedServicesList: PicnicServiceDetailItem[] = [];

  /**
   * Flag indicating whether operational services are actively being fetched.
   */
  public isLoadingServices: boolean = false;

  /**
   * Error message if on-demand query retrieval fails.
   */
  public errorMessage: string | null = null;

  /**
   * Flag indicating whether operational services have been successfully loaded.
   */
  public hasLoadedServicesSuccessfully: boolean = false;

  /**
   * Resolves the original operational service summary from the injected row data.
   * Delegated to shared operational-services utility.
   */
  public get originalSummary(): PicnicOperationalServiceSummary | undefined {
    return resolveOriginalServiceSummary(
      this.modalInjectData?.picnicItemRow,
      this.modalInjectData?.modalType,
    );
  }

  /**
   * Formats the operational date of the row to DD/MM/YYYY for presentation in modal header.
   * Delegated to shared date-formatting utility.
   */
  public get formattedOperationDate(): string {
    return formatLocalDateToDisplay(
      this.modalInjectData?.picnicItemRow?.fechaOperacion,
    );
  }

  /**
   * Backward-compatible alias for template binding.
   */
  public get formattedFechaOperacion(): string {
    return this.formattedOperationDate;
  }

  /**
   * Resolves the activity name of the picnic row.
   */
  public get activityName(): string {
    return (
      this.modalInjectData?.picnicItemRow?.activityName ||
      this.modalInjectData?.picnicItemRow?.servicioNombre ||
      ''
    );
  }

  /**
   * Initializes the operational services dialog with dependencies and injected data.
   *
   * Description:
   *   Injects modal data, picnic API service, snackbar notification service,
   *   dialog coordinator, and change detection controller.
   *
   * Parameters:
   *   dialogReference: MatDialogRef<ServiciosOperativosModalComponent> - Reference to control and close the dialog.
   *   modalInjectData: ServiciosOperativosModalData - Injected payload containing row information and service collections.
   *   picnicService: PicnicService - API service for picnic operations.
   *   snackBarService: MatSnackBar - Material Snackbar service for user feedback.
   *   matDialog: MatDialog - Angular Material dialog service to open provider and OS modals.
   *   changeDetectorRef: ChangeDetectorRef - Change detector to trigger view updates upon data mutations.
   *
   * Return Value:
   *   ServiciosOperativosModalComponent - Initialized component instance.
   *
   * Exceptions & Errors:
   *   None.
   */
  constructor(
    public readonly dialogReference: MatDialogRef<ServiciosOperativosModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public readonly modalInjectData: ServiciosOperativosModalData,
    private readonly picnicService: PicnicService,
    private readonly snackBarService: MatSnackBar,
    private readonly matDialog: MatDialog,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {}

  /**
   * Lifecycle hook executed after component initialization.
   *
   * Description:
   *   Loads operational services on demand for the specified activity and category via Core API.
   *   Pre-selects single service item when only one is available.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   None.
   */
  public ngOnInit(): void {
    this.dialogReference.disableClose = true;

    this.dialogReference
      .backdropClick()
      .pipe(takeUntil(this._unsubscribeOnDestroy$))
      .subscribe(() => {
        this.handleCloseModal();
      });

    this.dialogReference
      .keydownEvents()
      .pipe(takeUntil(this._unsubscribeOnDestroy$))
      .subscribe((keyboardEvent: KeyboardEvent) => {
        if (keyboardEvent.key === 'Escape') {
          this.handleCloseModal();
        }
      });

    if (
      this.modalInjectData.servicesList &&
      this.modalInjectData.servicesList.length > 0
    ) {
      this.hasLoadedServicesSuccessfully = true;
      this.loadedServicesList = [...this.modalInjectData.servicesList];
      if (
        this.loadedServicesList.length === 1 &&
        this.loadedServicesList[0]?.id &&
        this.isServiceSelectable(this.loadedServicesList[0])
      ) {
        this.selectedServiceIdentifiersSet.add(this.loadedServicesList[0].id);
      }
      return;
    }

    this.loadOperationalServicesOnDemand();
  }

  /**
   * Loads operational services on-demand via the Core GraphQL API query getServicesByActivityTipoCategoria.
   *
   * Description:
   *   Queries real-time service assignments and full order metadata (orderId, orderNumber, orderStatus,
   *   scheduledPaymentDate, pdfStatus) matching the activity ID and category filters.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   Gracefully catches network and GraphQL errors and presents retryable error prompt.
   */
  public loadOperationalServicesOnDemand(): void {
    const targetActivityId = this.modalInjectData.picnicItemRow?.idActivity;
    if (!targetActivityId) {
      this.isLoadingServices = false;
      this.hasLoadedServicesSuccessfully = false;
      this.errorMessage =
        'No se encontró el identificador de actividad para cargar los servicios operativos.';
      this.changeDetectorRef.markForCheck();
      this.changeDetectorRef.detectChanges();
      return;
    }

    this.isLoadingServices = true;
    this.hasLoadedServicesSuccessfully = false;
    this.errorMessage = null;
    this.changeDetectorRef.markForCheck();
    this.changeDetectorRef.detectChanges();

    const operationalFilters = resolveOperationalServiceFilters(
      this.modalInjectData?.modalType,
    );

    this.picnicService
      .queryGetServicesByActivityTipoCategoria({
        idActivity: targetActivityId,
        filters: operationalFilters,
      })
      .pipe(takeUntil(this._unsubscribeOnDestroy$))
      .subscribe({
        next: (queryResponsePayload) => {
          this.isLoadingServices = false;
          this.hasLoadedServicesSuccessfully = true;
          const retrievedServices =
            queryResponsePayload?.getServicesByActivityTipoCategoria || [];

          this.loadedServicesList = retrievedServices.map((serviceItem) => {
            const resolvedOrderId = serviceItem.order?.orderId || null;
            const resolvedOrderNumber = serviceItem.order?.orderNumber || null;
            const resolvedOrderStatus = serviceItem.order?.orderStatus || null;

            return {
              id: serviceItem.id,
              nombreServicio: serviceItem.nombreServicio,
              tipoServicio: serviceItem.tipoServicio,
              categoriaServicio: serviceItem.categoriaServicio,
              nombreProveedor:
                serviceItem.providerSnapshot?.nombre ||
                'Sin proveedor asignado',
              idProveedor: serviceItem.providerSnapshot?.id || undefined,
              locacion: serviceItem.serviceSnapshot?.locacion || undefined,
              ssffproduct: serviceItem.ssffproduct,
              serviceAdd: serviceItem.serviceAdd,
              productId: serviceItem.productId,
              isSSFFPadre:
                (serviceItem as any).isSSFFPadre ??
                this.modalInjectData.picnicItemRow?.isSSFFPadre,
              orderId: resolvedOrderId,
              orderNumber: resolvedOrderNumber,
              ordenServicioId: resolvedOrderId,
              ordenServicioStatus: resolvedOrderStatus,
              order: serviceItem.order
                ? {
                    orderId: serviceItem.order.orderId || '',
                    orderNumber: serviceItem.order.orderNumber,
                    displayCode: serviceItem.order.displayCode,
                    orderStatus: serviceItem.order.orderStatus,
                    scheduledPaymentDate:
                      serviceItem.order.scheduledPaymentDate,
                    freezeDeadlineAt: serviceItem.order.freezeDeadlineAt,
                    pdfStatus: serviceItem.order.pdfStatus,
                    pdfGeneratedAt: serviceItem.order.pdfGeneratedAt,
                    lastPdfError: serviceItem.order.lastPdfError,
                  }
                : null,
            };
          });

          if (
            this.loadedServicesList.length === 1 &&
            this.loadedServicesList[0]?.id &&
            this.isServiceSelectable(this.loadedServicesList[0])
          ) {
            this.selectedServiceIdentifiersSet.add(
              this.loadedServicesList[0].id,
            );
          }
          this.changeDetectorRef.markForCheck();
          this.changeDetectorRef.detectChanges();
        },
        error: (queryError) => {
          this.isLoadingServices = false;
          this.hasLoadedServicesSuccessfully = false;
          this.errorMessage =
            'No se pudieron cargar los servicios operativos para esta actividad.';
          console.error(
            '[ServiciosOperativosModalComponent] Error loading operational services via Core API:',
            queryError,
          );
          this.changeDetectorRef.markForCheck();
          this.changeDetectorRef.detectChanges();
        },
      });
  }

  /**
   * Lifecycle hook executed when component is destroyed.
   * Cleans up active RxJS subscriptions.
   */
  public ngOnDestroy(): void {
    this._unsubscribeOnDestroy$.next(true);
    this._unsubscribeOnDestroy$.complete();
  }

  /**
   * Resolves the modal title based on the operational category or custom override.
   *
   * Description:
   *   Determines header text for transport equipment, picnic assistants, or bathroom services.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   string - Title text for the modal header.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get modalTitle(): string {
    return resolveOperationalServiceMetadata(
      this.modalInjectData?.modalType,
      this.modalInjectData?.customTitle,
      this.modalInjectData?.customIcon,
    ).title;
  }

  /**
   * Resolves the icon name corresponding to the operational category.
   * Delegated to shared operational-services utility.
   *
   * @returns Tabler icon name string.
   */
  public get modalIcon(): string {
    return resolveOperationalServiceMetadata(
      this.modalInjectData?.modalType,
      this.modalInjectData?.customTitle,
      this.modalInjectData?.customIcon,
    ).icon;
  }

  /**
   * Retrieves the operational services collection to display.
   *
   * Description:
   *   Extracts the specific array of service items according to category from parent row.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   PicnicServiceDetailItem[] - List of PicnicServiceDetailItem items.
   *
   * Exceptions & Errors:
   *   None; returns empty array when no services are available.
   */
  public get servicesList(): PicnicServiceDetailItem[] {
    if (this.loadedServicesList && this.loadedServicesList.length > 0) {
      return this.loadedServicesList;
    }
    if (
      this.modalInjectData.servicesList &&
      this.modalInjectData.servicesList.length > 0
    ) {
      return this.modalInjectData.servicesList;
    }
    return this.loadedServicesList || [];
  }

  /**
   * Checks whether a specific service is currently selected.
   *
   * Description:
   *   Evaluates membership in selectedServiceIdentifiersSet.
   *
   * Parameters:
   *   serviceIdentifier: string - Primary key ID of the service.
   *
   * Return Value:
   *   boolean - True if selected, false otherwise.
   *
   * Exceptions & Errors:
   *   None.
   */
  public isServiceSelected(serviceIdentifier: string): boolean {
    if (
      this.servicesList.length === 1 &&
      this.isServiceSelectable(this.servicesList[0]) &&
      this.servicesList[0]?.id === serviceIdentifier
    ) {
      return true;
    }
    return this.selectedServiceIdentifiersSet.has(serviceIdentifier);
  }

  /**
   * Evaluates whether the current itinerary/file context is a master parent file (isSSFFPadre = true).
   *
   * Description:
   *   Checks the isSSFFPadre property on the injected picnicItemRow context.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   boolean - True if parent file / Salida Fija Compartida; false if child / individual file.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get isParentFile(): boolean {
    return Boolean(this.modalInjectData.picnicItemRow?.isSSFFPadre);
  }

  /**
   * Determines whether an operational service belongs to the shared SSFF catalog on a child file
   * and must be blocked from OS generation, provider changing, and price/date edition.
   *
   * Description:
   *   Applies the domain business rule from operations-app:
   *   - In a master Parent File (isSSFFPadre = true): Shared catalog services are NOT blocked.
   *   - In a Child File (isSSFFPadre = false/null): Standard catalog services (ssffproduct && productId && !serviceAdd)
   *     are BLOCKED because their OS is emitted and paid centrally from the Parent File.
   *   - Additional private services (serviceAdd = true) or upgrades (ssffproduct && !productId) are NOT blocked.
   *
   * Parameters:
   *   serviceRecord: PicnicServiceDetailItem | null | undefined - The candidate service item.
   *   isParentFileFlag: boolean (optional) - Explicit parent file flag override. Defaults to component isParentFile.
   *
   * Return Value:
   *   boolean - True if blocked on child file; false if eligible/operable.
   *
   * Exceptions & Errors:
   *   None.
   */
  public isSsffCatalogBlockedOnChild(
    serviceRecord: PicnicServiceDetailItem | null | undefined,
    isParentFileFlag: boolean = this.isParentFile,
  ): boolean {
    if (isParentFileFlag || Boolean(serviceRecord?.isSSFFPadre)) {
      return false;
    }
    if (Boolean(serviceRecord?.serviceAdd)) {
      return false;
    }
    // Upgrade: ssffproduct sin productId
    if (Boolean(serviceRecord?.ssffproduct) && !serviceRecord?.productId) {
      return false;
    }
    return (
      Boolean(serviceRecord?.ssffproduct) && Boolean(serviceRecord?.productId)
    );
  }

  /**
   * Helper evaluating whether the service is blocked due to child file SSFF restrictions.
   *
   * Description:
   *   Convenience method wrapping isSsffCatalogBlockedOnChild with the active parent file context.
   *
   * Parameters:
   *   serviceRecord: PicnicServiceDetailItem - The target service item.
   *
   * Return Value:
   *   boolean - True if blocked by SSFF child restrictions; false otherwise.
   *
   * Exceptions & Errors:
   *   None.
   */
  public isSsffBlocked(serviceRecord: PicnicServiceDetailItem): boolean {
    return this.isSsffCatalogBlockedOnChild(serviceRecord, this.isParentFile);
  }

  /**
   * Evaluates whether to display the SSFF indicator chip for a service item.
   *
   * Description:
   *   In Parent Files: Displays for non-additional services.
   *   In Child Files: Displays for standard catalog services with productId.
   *
   * Parameters:
   *   serviceRecord: PicnicServiceDetailItem - The service item to evaluate.
   *
   * Return Value:
   *   boolean - True if chip should be rendered; false otherwise.
   *
   * Exceptions & Errors:
   *   None.
   */
  public showSsffChip(serviceRecord: PicnicServiceDetailItem): boolean {
    if (this.isParentFile) {
      return !serviceRecord?.serviceAdd;
    }
    return (
      !serviceRecord?.serviceAdd &&
      Boolean(serviceRecord?.ssffproduct) &&
      Boolean(serviceRecord?.productId)
    );
  }

  /**
   * Evaluates whether to display the Upgrade indicator chip for an upgraded service item.
   *
   * Description:
   *   Displays for items marked as ssffproduct without a catalog productId and not an explicit serviceAdd.
   *
   * Parameters:
   *   serviceRecord: PicnicServiceDetailItem - The service item to evaluate.
   *
   * Return Value:
   *   boolean - True if upgrade chip should be rendered; false otherwise.
   *
   * Exceptions & Errors:
   *   None.
   */
  public showUpgradeChip(serviceRecord: PicnicServiceDetailItem): boolean {
    return (
      Boolean(serviceRecord?.ssffproduct) &&
      !serviceRecord?.productId &&
      !serviceRecord?.serviceAdd
    );
  }

  /**
   * Evaluates whether a service item can be selected for provider assignment or OS generation.
   *
   * Description:
   *   Services that belong to the shared SSFF catalog on a Child File cannot be selected,
   *   and services with an active Order of Service cannot be selected.
   *
   * Parameters:
   *   serviceDetailRecord: PicnicServiceDetailItem - Target service item.
   *
   * Return Value:
   *   boolean - True if service is selectable (not blocked by SSFF and NO active order); false otherwise.
   *
   * Exceptions & Errors:
   *   None.
   */
  public isServiceSelectable(
    serviceDetailRecord: PicnicServiceDetailItem,
  ): boolean {
    if (this.isSsffBlocked(serviceDetailRecord)) {
      return false;
    }
    return !this.hasServiceOrder(serviceDetailRecord);
  }

  /**
   * Retrieves the collection of services eligible for selection (those without active OS).
   *
   * Description:
   *   Filters servicesList retaining only items where hasServiceOrder is false.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   PicnicServiceDetailItem[] - Array of selectable service items.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get selectableServicesList(): PicnicServiceDetailItem[] {
    return this.servicesList.filter((serviceItem) =>
      this.isServiceSelectable(serviceItem),
    );
  }

  /**
   * Retrieves count of selectable services without an active OS.
   *
   * Description:
   *   Returns length of selectableServicesList.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   number - Count of selectable services.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get selectableServicesCount(): number {
    return this.selectableServicesList.length;
  }

  /**
   * Evaluates whether every service in the modal has an active Order of Service.
   *
   * Description:
   *   Checks that servicesList is not empty and every item has an active order ID.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   boolean - True if all services have an active order; false otherwise.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get allServicesHaveOrders(): boolean {
    return (
      this.servicesList.length > 0 &&
      this.servicesList.every((serviceItem) =>
        this.hasServiceOrder(serviceItem),
      )
    );
  }

  /**
   * Toggles the selection state of a service item.
   *
   * Description:
   *   Adds or removes the service ID from selectedServiceIdentifiersSet if selectable.
   *
   * Parameters:
   *   serviceDetailRecord: PicnicServiceDetailItem - The service record to toggle.
   *   clickEvent: MouseEvent (optional) - Triggering mouse event to stop bubbling when needed.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   None.
   */
  public toggleServiceSelection(
    serviceDetailRecord: PicnicServiceDetailItem,
    clickEvent?: MouseEvent,
  ): void {
    if (clickEvent) {
      clickEvent.stopPropagation();
    }
    if (
      !serviceDetailRecord ||
      !serviceDetailRecord.id ||
      !this.isServiceSelectable(serviceDetailRecord)
    ) {
      return;
    }

    if (this.selectedServiceIdentifiersSet.has(serviceDetailRecord.id)) {
      this.selectedServiceIdentifiersSet.delete(serviceDetailRecord.id);
    } else {
      this.selectedServiceIdentifiersSet.add(serviceDetailRecord.id);
    }
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Toggles selection across all available selectable services in the modal.
   *
   * Description:
   *   Selects all selectable services if not all are currently selected; otherwise deselects all.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   None.
   */
  public toggleSelectAllServices(): void {
    if (this.areAllServicesSelected()) {
      this.selectedServiceIdentifiersSet.clear();
    } else {
      for (const serviceRecord of this.selectableServicesList) {
        if (serviceRecord?.id) {
          this.selectedServiceIdentifiersSet.add(serviceRecord.id);
        }
      }
    }
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Checks whether all available selectable services are currently selected.
   *
   * Description:
   *   Compares selected count against selectable services count.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   boolean - True if all selectable services are selected.
   *
   * Exceptions & Errors:
   *   None.
   */
  public areAllServicesSelected(): boolean {
    const selectableCount = this.selectableServicesCount;
    return (
      selectableCount > 0 &&
      this.selectedServiceIdentifiersSet.size === selectableCount
    );
  }

  /**
   * Checks whether the current selection is indeterminate (some but not all selectable chosen).
   *
   * Description:
   *   Used by master checkbox to display indeterminate state.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   boolean - True if partially selected.
   *
   * Exceptions & Errors:
   *   None.
   */
  public isSelectionIndeterminate(): boolean {
    const selectedCount = this.selectedServiceIdentifiersSet.size;
    const selectableCount = this.selectableServicesCount;
    return selectedCount > 0 && selectedCount < selectableCount;
  }

  /**
   * Retrieves the array of PicnicServiceDetailItem items that are currently selected.
   *
   * Description:
   *   Filters servicesList by active selection IDs and verifies selectable status.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   PicnicServiceDetailItem[] - Array of selected service items.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get selectedServicesCollection(): PicnicServiceDetailItem[] {
    const selectable = this.selectableServicesList;
    if (this.servicesList.length === 1 && selectable.length === 1) {
      return selectable;
    }
    return selectable.filter((serviceRecord) =>
      this.selectedServiceIdentifiersSet.has(serviceRecord.id),
    );
  }

  /**
   * Retrieves the count of currently selected services.
   *
   * Description:
   *   Returns the size of selectedServiceIdentifiersSet.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   number - Total number of selected services.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get selectedServicesCount(): number {
    return this.selectedServicesCollection.length;
  }

  /**
   * Evaluates whether changing provider is allowed for the current selection.
   *
   * Description:
   *   Returns true if at least one selected service is eligible and has no active OS.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   boolean - True if eligible to change provider; false otherwise.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get canChangeProviderForSelection(): boolean {
    return (
      this.selectedServicesCount > 0 &&
      this.selectedServicesCollection.every((serviceRecord) =>
        this.isServiceSelectable(serviceRecord),
      )
    );
  }

  /**
   * Resolves dynamic tooltip text for the "Seleccionar Proveedor" action button.
   *
   * Description:
   *   Explains whether provider selection is enabled or blocked due to existing OS.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   string - Human-readable tooltip explanation string.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get changeProviderTooltipText(): string {
    const hasSsffBlockedItems = this.servicesList.some((serviceRecord) =>
      this.isSsffBlocked(serviceRecord),
    );
    if (
      this.servicesList.length === 1 &&
      this.isSsffBlocked(this.servicesList[0])
    ) {
      return 'Servicio de Salida Fija Compartida: El proveedor debe ser asignado desde el File Padre';
    }
    if (
      this.servicesList.length === 1 &&
      this.hasServiceOrder(this.servicesList[0])
    ) {
      return 'No se puede cambiar proveedor porque el servicio ya cuenta con Orden de Servicio';
    }
    if (this.selectableServicesCount === 0) {
      return hasSsffBlockedItems
        ? 'Los servicios de Salida Fija deben gestionarse desde el File Padre'
        : 'Todos los servicios cuentan con Orden de Servicio generada';
    }
    if (this.selectedServicesCount === 0) {
      return 'Selecciona al menos un servicio disponible para cambiar proveedor';
    }
    return `Seleccionar proveedor para los servicios seleccionados (${this.selectedServicesCount})`;
  }

  /**
   * Retrieves the collection of currently selected services that do not have an active Order of Service.
   *
   * Description:
   *   Filters selected services collection retaining only items where hasServiceOrder is false.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   PicnicServiceDetailItem[] - Array of selected service items eligible for new order creation.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get selectedServicesEligibleForOrderGeneration(): PicnicServiceDetailItem[] {
    return this.selectedServicesCollection.filter(
      (serviceDetailRecord) =>
        !this.hasServiceOrder(serviceDetailRecord) &&
        !this.isSsffBlocked(serviceDetailRecord),
    );
  }

  /**
   * Retrieves the total count of currently selected services eligible for Order of Service generation.
   *
   * Description:
   *   Returns the length of selectedServicesEligibleForOrderGeneration array.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   number - Count of selected services without an active order.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get selectedServicesEligibleForOrderCount(): number {
    return this.selectedServicesEligibleForOrderGeneration.length;
  }

  /**
   * Evaluates whether the "Generar OS" action is enabled for the current selection.
   *
   * Description:
   *   Returns true if at least one selected service does not yet have an active Order of Service.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   boolean - True if eligible for generation; false otherwise.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get canGenerateOrderForSelection(): boolean {
    return this.selectedServicesEligibleForOrderCount > 0;
  }

  /**
   * Resolves dynamic tooltip text for the "Generar OS" bulk action button.
   *
   * Description:
   *   Provides contextual message explaining button status based on selection and existing orders.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   string - Human-readable tooltip explanation string.
   *
   * Exceptions & Errors:
   *   None.
   */
  public get orderGenerationTooltipText(): string {
    const hasSsffBlockedItems = this.servicesList.some((serviceRecord) =>
      this.isSsffBlocked(serviceRecord),
    );
    if (
      this.servicesList.length === 1 &&
      this.isSsffBlocked(this.servicesList[0])
    ) {
      return 'Servicio de Salida Fija Compartida: La Orden de Servicio debe ser emitida desde el File Padre';
    }
    if (this.selectedServicesCount === 0) {
      return hasSsffBlockedItems && this.selectableServicesCount === 0
        ? 'Servicio(s) de Salida Fija deben emitirse desde el File Padre'
        : 'Selecciona al menos un servicio para generar la Orden de Servicio';
    }
    if (this.selectedServicesEligibleForOrderCount === 0) {
      return 'Los servicios seleccionados ya cuentan con una Orden de Servicio';
    }
    return `Generar Orden de Servicio para los servicios seleccionados sin O.S. (${this.selectedServicesEligibleForOrderCount})`;
  }

  /**
   * Checks whether a service item has an active Order of Service.
   *
   * Description:
   *   Evaluates getServiceOrderId for truthiness.
   *
   * Parameters:
   *   serviceDetailRecord: PicnicServiceDetailItem - Target service item.
   *
   * Return Value:
   *   boolean - True if service has an active orderId.
   *
   * Exceptions & Errors:
   *   None.
   */
  public hasServiceOrder(
    serviceDetailRecord: PicnicServiceDetailItem,
  ): boolean {
    return Boolean(getServiceOrderId(serviceDetailRecord));
  }

  /**
   * Retrieves the order number string or numeric code from a service item.
   *
   * Description:
   *   Extracts order number via getServiceOrderNumber.
   *
   * Parameters:
   *   serviceDetailRecord: PicnicServiceDetailItem - Target service item.
   *
   * Return Value:
   *   string | number | null - Order number or null.
   *
   * Exceptions & Errors:
   *   None.
   */
  public resolveServiceOrderNumber(
    serviceDetailRecord: PicnicServiceDetailItem,
  ): string | number | null {
    return getServiceOrderNumber(serviceDetailRecord);
  }

  /**
   * Resolves the current order status string for a service item.
   *
   * Description:
   *   Checks order.orderStatus or ordenServicioStatus, falling back to 'CONFIRMADO'.
   *
   * Parameters:
   *   serviceDetailRecord: PicnicServiceDetailItem - Target service item.
   *
   * Return Value:
   *   string - Order status string.
   *
   * Exceptions & Errors:
   *   None.
   */
  public resolveServiceOrderStatus(
    serviceDetailRecord: PicnicServiceDetailItem,
  ): string {
    return (
      serviceDetailRecord?.order?.orderStatus ||
      serviceDetailRecord?.ordenServicioStatus ||
      ''
    );
  }

  public isServiceOrderPreliminary(
    serviceDetailRecord: PicnicServiceDetailItem,
  ): boolean {
    return isOrderStatusPreliminary(
      this.resolveServiceOrderStatus(serviceDetailRecord),
    );
  }

  public canShowServiceOrderPdf(
    serviceDetailRecord: PicnicServiceDetailItem,
  ): boolean {
    return canShowOrderPdf(serviceDetailRecord);
  }

  public resolveFreezeDeadlineDisplay(
    serviceDetailRecord: PicnicServiceDetailItem,
  ): string {
    return formatScheduledPaymentDateDisplay(
      serviceDetailRecord?.order?.freezeDeadlineAt,
    );
  }

  /**
   * Checks whether the service item's order is eligible for deletion.
   *
   * Description:
   *   Evaluates isOrderStatusDeletable against current order status.
   *
   * Parameters:
   *   serviceDetailRecord: PicnicServiceDetailItem - Target service item.
   *
   * Return Value:
   *   boolean - True if deletable (CONFIRMADO).
   *
   * Exceptions & Errors:
   *   None.
   */
  public isServiceOrderDeletable(
    serviceDetailRecord: PicnicServiceDetailItem,
  ): boolean {
    if (this.isSsffBlocked(serviceDetailRecord)) {
      return false;
    }
    const activeOrderStatus =
      this.resolveServiceOrderStatus(serviceDetailRecord);
    return isOrderStatusDeletable(activeOrderStatus);
  }

  /**
   * Evaluates whether an order's scheduled payment date can be edited (must have valid orderId and be in CONFIRMADO status).
   *
   * Description:
   *   Validates that order exists, is not blocked by child SSFF restrictions, and orderStatus is CONFIRMADO.
   *
   * Parameters:
   *   serviceDetailRecord: PicnicServiceDetailItem - Target service item.
   *
   * Return Value:
   *   boolean - True if editable; false otherwise.
   *
   * Exceptions & Errors:
   *   None.
   */
  public isServiceOrderEditable(
    serviceDetailRecord: PicnicServiceDetailItem,
  ): boolean {
    if (this.isSsffBlocked(serviceDetailRecord)) {
      return false;
    }
    return isOrderEditable(serviceDetailRecord);
  }

  /**
   * Resolves the scheduled payment date display string for a service item.
   *
   * Description:
   *   Formats date as DD/MM/YYYY or returns placeholder hyphen.
   *
   * Parameters:
   *   serviceDetailRecord: PicnicServiceDetailItem - Target service item.
   *
   * Return Value:
   *   string - Formatted date string.
   *
   * Exceptions & Errors:
   *   None.
   */
  public resolveScheduledPaymentDateDisplay(
    serviceDetailRecord: PicnicServiceDetailItem,
  ): string {
    const rawDate = serviceDetailRecord?.order?.scheduledPaymentDate;
    return formatScheduledPaymentDateDisplay(rawDate);
  }

  /**
   * Evaluates whether an order's PDF has been successfully generated and is ready for viewing.
   *
   * Description:
   *   Validates whether pdfStatus matches GENERATED state using domain helper isPdfReady.
   *
   * Parameters:
   *   rawPdfStatus: string | null | undefined - The raw PDF status string to check.
   *
   * Return Value:
   *   boolean - True if PDF is generated and ready; false otherwise.
   *
   * Exceptions & Errors:
   *   None.
   */
  public isServicePdfReady(rawPdfStatus: string | null | undefined): boolean {
    return isPdfReady(rawPdfStatus);
  }

  /**
   * Opens the DialogEditFechaPagoProgramadaComponent to edit the scheduled payment date of the service order.
   *
   * Description:
   *   Opens calendar dialog, applies updated date to local order model upon mutation success,
   *   or refreshes data if conflict occurs.
   *
   * Parameters:
   *   targetServiceDetailItem: PicnicServiceDetailItem - Target service item holding order reference.
   *   clickEvent: MouseEvent (optional) - Mouse click event to stop bubbling.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   Displays warning snackbar if order is not in editable status.
   */
  public handleOpenEditScheduledPaymentDate(
    targetServiceDetailItem: PicnicServiceDetailItem,
    clickEvent?: MouseEvent,
  ): void {
    if (clickEvent) {
      clickEvent.stopPropagation();
    }

    if (!this.isServiceOrderEditable(targetServiceDetailItem)) {
      const activeStatus = this.resolveServiceOrderStatus(
        targetServiceDetailItem,
      );
      this.snackBarService.open(
        getOrderMutationBlockedMessage('editar', activeStatus),
        'Cerrar',
        { duration: 4000 },
      );
      return;
    }

    const editDialogReference = this.matDialog.open(
      DialogEditFechaPagoProgramadaComponent,
      buildEditFechaPagoDialogConfig({
        service: targetServiceDetailItem,
      }),
    );

    editDialogReference
      .afterClosed()
      .pipe(takeUntil(this._unsubscribeOnDestroy$))
      .subscribe((dialogResult) => {
        if (dialogResult && dialogResult.success) {
          const scheduledPaymentDate =
            dialogResult.order?.scheduledPaymentDate ??
            (dialogResult.newDate
              ? formatScheduledPaymentDate(
                  dialogResult.newDate instanceof Date
                    ? dialogResult.newDate
                    : new Date(dialogResult.newDate),
                )
              : null);

          applyOrderUpdateToServices(
            this.servicesList,
            dialogResult.orderNumber ??
              targetServiceDetailItem.order?.orderNumber,
            dialogResult.order ?? { scheduledPaymentDate },
            targetServiceDetailItem,
          );

          this.changeDetectorRef.markForCheck();
          this.changeDetectorRef.detectChanges();
        } else if (dialogResult && dialogResult.shouldRefresh) {
          this.loadOperationalServicesOnDemand();
        }
      });
  }

  /**
   * Opens the DialogProviderMultipleComponent to update provider for all currently selected services.
   *
   * Description:
   *   Validates selection, opens provider modal, and updates assigned provider name/ID on success.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   Displays warning snackbar if no services are selected.
   */
  public handleOpenChangeProviderForSelected(): void {
    const activeSelectedServices = this.selectedServicesCollection;
    if (activeSelectedServices.length === 0) {
      this.snackBarService.open(
        'Selecciona al menos un servicio para cambiar el proveedor',
        'Cerrar',
        { duration: 3000 },
      );
      return;
    }

    const constructedModalTitle =
      activeSelectedServices.length === 1
        ? `Cambiar Proveedor: ${activeSelectedServices[0].nombreServicio || activeSelectedServices[0].tipoServicio}`
        : `Cambiar Proveedor (${activeSelectedServices.length} servicios seleccionados)`;

    const providerDialogReference = this.matDialog.open(
      DialogProviderMultipleComponent,
      {
        width: '560px',
        maxWidth: '92vw',
        disableClose: true,
        data: {
          filtered: activeSelectedServices.map((serviceDetailItem) => ({
            id: serviceDetailItem.id,
            nombreServicio: serviceDetailItem.nombreServicio,
            tipoServicio: serviceDetailItem.tipoServicio,
            categoriaServicio: serviceDetailItem.categoriaServicio,
            locacion:
              serviceDetailItem.locacion ||
              serviceDetailItem.serviceSnapshot?.locacion ||
              '',
            serviceSnapshot: serviceDetailItem.serviceSnapshot,
            nombreProveedor: serviceDetailItem.nombreProveedor,
          })),
          modalTitle: constructedModalTitle,
        },
      },
    );

    providerDialogReference
      .afterClosed()
      .pipe(takeUntil(this._unsubscribeOnDestroy$))
      .subscribe((dialogResult) => {
        if (dialogResult && dialogResult.success) {
          const updatedProviderNameString =
            dialogResult.nombreProveedor || dialogResult.providerName || '';
          const updatedProviderIdentifierString =
            dialogResult.idProveedor || dialogResult.providerId || '';

          const selectedIdsLookupSet = new Set(
            activeSelectedServices.map((serviceItem) => serviceItem.id),
          );

          // Update local items in servicesList
          for (const localServiceItem of this.servicesList) {
            if (selectedIdsLookupSet.has(localServiceItem.id)) {
              if (updatedProviderNameString) {
                localServiceItem.nombreProveedor = updatedProviderNameString;
              }
              if (updatedProviderIdentifierString) {
                localServiceItem.idProveedor = updatedProviderIdentifierString;
              }
            }
          }

          // Retain selection for single service or still-selectable items
          if (
            this.servicesList.length === 1 &&
            this.isServiceSelectable(this.servicesList[0]) &&
            this.servicesList[0]?.id
          ) {
            this.selectedServiceIdentifiersSet.add(this.servicesList[0].id);
          }

          this.changeDetectorRef.markForCheck();
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  /**
   * Opens the CreateOrderComponent modal for all currently selected services.
   *
   * Description:
   *   Collects selected service IDs, opens CreateOrder dialog, and synchronizes memory on success.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   Displays warning snackbar if no services are selected.
   */
  public handleOpenCreateOrderForSelected(): void {
    const eligibleSelectedServices =
      this.selectedServicesEligibleForOrderGeneration;

    if (this.selectedServicesCount === 0) {
      this.snackBarService.open(
        'Selecciona al menos un servicio para generar la Orden de Servicio',
        'Cerrar',
        { duration: 3000 },
      );
      return;
    }

    if (eligibleSelectedServices.length === 0) {
      this.snackBarService.open(
        'Los servicios seleccionados ya cuentan con una Orden de Servicio',
        'Cerrar',
        { duration: 3500 },
      );
      return;
    }

    const targetServiceIdsArray = eligibleSelectedServices.map(
      (serviceItem) => serviceItem.id,
    );
    const operationDates = eligibleSelectedServices.map(
      (serviceItem: any) =>
        serviceItem?.fechaOperacion ??
        serviceItem?.startDateService ??
        this.modalInjectData?.picnicItemRow?.fechaOperacion ??
        null,
    );

    const creationDialogReference = this.matDialog.open(
      CreateOrderComponent,
      buildCreateOrderDialogConfig({
        serviceIds: targetServiceIdsArray,
        operationDates,
        contextLabel: `File: ${this.modalInjectData.picnicItemRow.fileNumber} · ${targetServiceIdsArray.length} servicio(s)`,
      }),
    );

    creationDialogReference
      .afterClosed()
      .pipe(takeUntil(this._unsubscribeOnDestroy$))
      .subscribe((dialogResult: CreateOrderDialogResult | undefined) => {
        if (dialogResult && dialogResult.success) {
          const createdOrdersList = dialogResult.result?.createdOrders;

          applyCreatedOrdersToServices(
            this.servicesList,
            createdOrdersList,
            'id',
            dialogResult.submittedInput,
          );

          this.selectedServiceIdentifiersSet.clear();
          this.changeDetectorRef.markForCheck();
          this.changeDetectorRef.detectChanges();

          this.snackBarService.open(
            `Órdenes de Servicio creadas con éxito (${createdOrdersList?.length || 0})`,
            'Cerrar',
            { duration: 3500 },
          );
        } else if (dialogResult?.shouldRefresh) {
          this.loadOperationalServicesOnDemand();
        }
      });
  }

  /**
   * Opens the OrderPdfDialogComponent modal to view and download Order of Service PDF.
   *
   * Description:
   *   Extracts orderId, orderNumber, and PDF status, opening PDF viewer dialog.
   *
   * Parameters:
   *   targetServiceDetailItem: PicnicServiceDetailItem - Service item holding order reference.
   *   clickEvent: MouseEvent (optional) - Mouse event to stop propagation.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   Displays warning snackbar if orderId is missing.
   */
  public handleViewOrderPdf(
    targetServiceDetailItem: PicnicServiceDetailItem,
    clickEvent?: MouseEvent,
  ): void {
    if (clickEvent) {
      clickEvent.stopPropagation();
    }

    const resolvedOrderId = getServiceOrderId(targetServiceDetailItem);
    if (!resolvedOrderId) {
      this.snackBarService.open(
        'No se encontró una Orden de Servicio asociada a este servicio',
        'Cerrar',
        { duration: 3000 },
      );
      return;
    }

    const resolvedOrderNumber = getServiceOrderNumber(targetServiceDetailItem);
    const resolvedPdfStatus =
      targetServiceDetailItem.order?.pdfStatus ||
      targetServiceDetailItem.ordenServicioStatus;
    const resolvedLastPdfError = targetServiceDetailItem.order?.lastPdfError;

    const dialogConfiguration = new MatDialogConfig();
    dialogConfiguration.width = '880px';
    dialogConfiguration.maxWidth = '95vw';
    dialogConfiguration.autoFocus = false;
    dialogConfiguration.data = {
      orderId: resolvedOrderId,
      orderNumber: resolvedOrderNumber,
      pdfStatus: resolvedPdfStatus,
      lastPdfError: resolvedLastPdfError,
    };

    const pdfDialogReference = this.matDialog.open(
      OrderPdfDialogComponent,
      dialogConfiguration,
    );

    pdfDialogReference
      .afterClosed()
      .pipe(takeUntil(this._unsubscribeOnDestroy$))
      .subscribe((pdfDialogResult: OrderPdfDialogResult | undefined) => {
        if (pdfDialogResult?.pdfStatus && targetServiceDetailItem.order) {
          targetServiceDetailItem.order.pdfStatus = pdfDialogResult.pdfStatus;
          targetServiceDetailItem.order.pdfGeneratedAt =
            pdfDialogResult.pdfGeneratedAt ?? null;
          targetServiceDetailItem.order.lastPdfError =
            pdfDialogResult.lastPdfError ?? null;
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  /**
   * Abre el historial de recálculo de la OS (mismo icono que Ops Programación).
   */
  public handleOpenRecalcHistory(
    targetServiceDetailItem: PicnicServiceDetailItem,
    clickEvent?: MouseEvent,
  ): void {
    if (clickEvent) {
      clickEvent.stopPropagation();
    }
    const resolvedOrderId = getServiceOrderId(targetServiceDetailItem);
    if (!resolvedOrderId) {
      return;
    }
    this.matDialog.open(OrderRecalcHistoryDialogComponent, {
      width: '480px',
      maxWidth: '94vw',
      autoFocus: false,
      panelClass: 'os-history-panel',
      data: {
        orderId: resolvedOrderId,
        orderLabel: this.resolveServiceOrderNumber(targetServiceDetailItem),
        lastRecalculatedAt:
          targetServiceDetailItem.order?.lastRecalculatedAt ?? null,
        autoAdjusted: targetServiceDetailItem.order?.autoAdjusted === true,
      },
    });
  }

  /**
   * Confirma una OS preliminar (mismo flujo que Ops Programación).
   */
  public handleConfirmPreliminaryOrder(
    targetServiceDetailItem: PicnicServiceDetailItem,
    clickEvent?: MouseEvent,
  ): void {
    if (clickEvent) {
      clickEvent.stopPropagation();
    }

    const resolvedOrderId = getServiceOrderId(targetServiceDetailItem);
    if (!resolvedOrderId || !this.isServiceOrderPreliminary(targetServiceDetailItem)) {
      return;
    }

    const dialogReference = this.matDialog.open(OrderConfirmDialogComponent, {
      width: '420px',
      maxWidth: '94vw',
      panelClass: 'corte-dialog-panel',
      autoFocus: false,
      data: {
        orderId: resolvedOrderId,
        orderLabel: this.resolveServiceOrderNumber(targetServiceDetailItem),
      },
    });

    dialogReference
      .afterClosed()
      .pipe(takeUntil(this._unsubscribeOnDestroy$))
      .subscribe((result: ConfirmPreliminaryOrderResult | undefined) => {
        if (!result) {
          return;
        }
        applyConfirmedPreliminaryOrder(this.servicesList, result);
        this.changeDetectorRef.markForCheck();
        this.changeDetectorRef.detectChanges();
        this.snackBarService.open('Orden de Servicio confirmada', 'Cerrar', {
          duration: 3000,
        });
      });
  }

  /**
   * Edita el plazo «Confirmar antes del» de una OS preliminar.
   */
  public handleOpenFreezeDeadline(
    targetServiceDetailItem: PicnicServiceDetailItem,
    clickEvent?: MouseEvent,
  ): void {
    if (clickEvent) {
      clickEvent.stopPropagation();
    }

    const resolvedOrderId = getServiceOrderId(targetServiceDetailItem);
    if (!resolvedOrderId || !this.isServiceOrderPreliminary(targetServiceDetailItem)) {
      return;
    }

    const dialogReference = this.matDialog.open(
      OrderFreezeDeadlineDialogComponent,
      {
        width: '420px',
        maxWidth: '94vw',
        panelClass: 'corte-dialog-panel',
        autoFocus: false,
        data: {
          orderId: resolvedOrderId,
          freezeDeadlineAt:
            targetServiceDetailItem.order?.freezeDeadlineAt ?? null,
          orderLabel: this.resolveServiceOrderNumber(targetServiceDetailItem),
        },
      },
    );

    dialogReference
      .afterClosed()
      .pipe(takeUntil(this._unsubscribeOnDestroy$))
      .subscribe((result: OrderFreezeDeadlineDialogResult | undefined) => {
        if (!result?.freezeDeadlineAt) {
          return;
        }
        const freezeDeadlineAt = result.freezeDeadlineAt.includes('T')
          ? result.freezeDeadlineAt
          : `${result.freezeDeadlineAt}T00:00:00.000Z`;
        applyFreezeDeadlineToServices(
          this.servicesList,
          {
            orderId: resolvedOrderId,
            orderNumber: targetServiceDetailItem.order?.orderNumber,
          },
          freezeDeadlineAt,
        );
        this.changeDetectorRef.markForCheck();
        this.changeDetectorRef.detectChanges();
        this.snackBarService.open(
          'Plazo para confirmar actualizado',
          'Cerrar',
          { duration: 3000 },
        );
      });
  }

  /**
   * Opens the DialogDeleteOSComponent to confirm and delete an Order of Service.
   *
   * Description:
   *   Opens delete dialog, invokes deleteOrder mutation, and cleans memory across collections on success.
   *
   * Parameters:
   *   targetServiceDetailItem: PicnicServiceDetailItem - Target service item.
   *   clickEvent: MouseEvent (optional) - Mouse event to stop propagation.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   Displays warning snackbar if order cannot be found or deleted.
   */
  public handleDeleteOrder(
    targetServiceDetailItem: PicnicServiceDetailItem,
    clickEvent?: MouseEvent,
  ): void {
    if (clickEvent) {
      clickEvent.stopPropagation();
    }

    const resolvedOrderId = getServiceOrderId(targetServiceDetailItem);
    if (!resolvedOrderId) {
      this.snackBarService.open(
        'Este servicio no tiene una Orden de Servicio asociada para eliminar',
        'Cerrar',
        { duration: 3000 },
      );
      return;
    }

    const consolidatedServicesArray = this.servicesList;

    const dialogConfiguration = new MatDialogConfig();
    dialogConfiguration.width = '480px';
    dialogConfiguration.maxWidth = '94vw';
    dialogConfiguration.panelClass = CREATE_ORDER_PANEL_CLASS;
    dialogConfiguration.autoFocus = false;
    dialogConfiguration.disableClose = false;
    dialogConfiguration.data = {
      service: targetServiceDetailItem,
      dataAll: consolidatedServicesArray,
      contextLabel: `File: ${this.modalInjectData.picnicItemRow.fileNumber}`,
    };

    const deleteDialogReference = this.matDialog.open(
      DialogDeleteOSComponent,
      dialogConfiguration,
    );

    deleteDialogReference
      .afterClosed()
      .pipe(takeUntil(this._unsubscribeOnDestroy$))
      .subscribe(
        (deletionDialogResult: DeleteOrderDialogResult | undefined) => {
          if (deletionDialogResult && deletionDialogResult.success) {
            applyDeleteOrderDialogResult(
              this.servicesList,
              deletionDialogResult,
              targetServiceDetailItem,
            );

            this.changeDetectorRef.markForCheck();
            this.changeDetectorRef.detectChanges();

            this.snackBarService.open(
              'Orden de Servicio eliminada con éxito',
              'Cerrar',
              { duration: 3000 },
            );
          } else if (deletionDialogResult?.shouldRefresh) {
            this.loadOperationalServicesOnDemand();
          }
        },
      );
  }

  /**
   * Closes the active modal dialog and emits updated summary statistics.
   *
   * Description:
   *   Calculates total count, generated orders count, and pending orders count from current state
   *   and passes them back to the parent table component.
   *
   * Parameters:
   *   None.
   *
   * Return Value:
   *   void.
   *
   * Exceptions & Errors:
   *   None.
   */
  public handleCloseModal(): void {
    if (
      !this.hasLoadedServicesSuccessfully ||
      this.isLoadingServices ||
      Boolean(this.errorMessage)
    ) {
      this.dialogReference.close({
        updatedSummary: this.originalSummary,
        modalType: this.modalInjectData.modalType,
      });
      return;
    }

    const isParentFileFlag = this.isParentFile;
    const operableServices = isParentFileFlag
      ? this.servicesList
      : this.servicesList.filter(
          (serviceItem) => !this.isSsffBlocked(serviceItem),
        );

    const totalCount =
      operableServices.length > 0
        ? operableServices.length
        : (this.originalSummary?.totalCount ?? 0);

    const generatedOrdersCount = operableServices.filter((serviceItem) =>
      Boolean(getServiceOrderId(serviceItem)),
    ).length;
    const preliminaryOrdersCount = operableServices.filter((serviceItem) =>
      this.isServiceOrderPreliminary(serviceItem),
    ).length;
    const pendingOrdersCount = Math.max(0, totalCount - generatedOrdersCount);

    this.dialogReference.close({
      updatedSummary: {
        totalCount,
        generatedOrdersCount,
        pendingOrdersCount,
        preliminaryOrdersCount,
      },
      modalType: this.modalInjectData.modalType,
    });
  }

  /**
   * Backward-compatible alias for handleOpenCreateOrderForSelected.
   */
  public handleOpenOrdenServicioForSelected(): void {
    this.handleOpenCreateOrderForSelected();
  }

  /**
   * Backward-compatible alias for handleViewOrderPdf.
   */
  public handleViewOrdenServicioPdf(
    targetServiceDetailItem: PicnicServiceDetailItem,
    clickEvent?: MouseEvent,
  ): void {
    this.handleViewOrderPdf(targetServiceDetailItem, clickEvent);
  }

  /**
   * Backward-compatible alias for handleDeleteOrder.
   */
  public handleDeleteOrdenServicio(
    targetServiceDetailItem: PicnicServiceDetailItem,
    clickEvent?: MouseEvent,
  ): void {
    this.handleDeleteOrder(targetServiceDetailItem, clickEvent);
  }
}
